/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { getUserPermissionGroupIds } from '../selectors/initiative';
import {
  AppIds,
  CompanyTrackerIds,
  DelegationWithouInitiativeIds,
  hasGroupAccess,
  PERMISSION_GROUPS,
  PortfolioTrackerIds
} from '../utils/permission-groups';
import { useAppSelector } from '../reducers';
import { generateUrl } from '../routes/util';
import { ROUTES } from '../constants/routes';
import { ClickHandlers } from '../routes/home/partials/cards';
import { navigateToRootApp } from '../routes/appRootMatcher';
import { useHistory } from 'react-router';
import { useOktaAuth } from '@okta/okta-react';
import { AppFlavour, getDefaultConfig } from '../config/app-config';
import { DisambiguationModalProps } from '../routes/home/partials/DisambiguationModal';
import { ReferralInfoType } from '../types/onboarding';

interface UseAppClickHandlersProps {
  toggleRequestDemoModal?: () => void;
  shouldOpenInNewTab?: boolean;
  setDisambiguationModal: (modal: DisambiguationModalProps | undefined) => void;
}

export const useAppClickHandlers = (props: UseAppClickHandlersProps): ClickHandlers => {
  const { toggleRequestDemoModal, shouldOpenInNewTab } = props

  const { oktaAuth } = useOktaAuth();
  const history = useHistory();

  // @TODO @CTL must be determined at build time?
  const defaultConfig = getDefaultConfig();
  const settings = defaultConfig.branding.ctlApp;

  const userPermissionIds = useAppSelector(getUserPermissionGroupIds);
  const surveyAvailable = useAppSelector((state) => state.surveyAvailable.data);
  const isLoggedIn = useAppSelector((state) => state.currentUser.loaded);
  const { data: rootInitiatives } = useAppSelector((state) => state.rootInitiatives);

  const hasDelegationAccess = (app: AppIds, userPermIds: string[] | Readonly<string[]>) => {
    // Have survey and trying to access CT/CTL
    if (surveyAvailable && [AppIds.CompanyTracker, AppIds.CompanyTrackerLight].includes(app)) {
      // but does not have access (must be delegation without initiative)
      return !hasGroupAccess(DelegationWithouInitiativeIds, userPermIds)
    }
    return false;
  }

  const redirect = (link: string, referralInfo?: ReferralInfoType) => {
    if (!shouldOpenInNewTab) {
      return history.push(link, referralInfo);
    }
    window.open(link, '_blank')
  }

  const publicLink = (link: string) => redirect(link);
  const showLoginModal = (link: string) => oktaAuth.signInWithRedirect({ originalUri: link });

  const premiumLink = (link: string, requiredPermissions: string | Readonly<string[]>, appId: AppIds) => {
    if (!isLoggedIn) {
      showLoginModal(link);
      return;
    }
    if (!hasGroupAccess(userPermissionIds, requiredPermissions) && !hasDelegationAccess(appId, userPermissionIds)) {
      toggleRequestDemoModal?.();
      return;
    }
    redirect(navigateToRootApp(rootInitiatives, appId))
  }

  const authRequiredLink = (link: string) => {
    if (!isLoggedIn) {
      showLoginModal(link);
      return;
    }
    redirect(link)
  }

  const GoToCompanyTrackerRegistration = (options?: { onboardingPath?: string; referralInfo?: ReferralInfoType }) => {
    const accessLink = () => premiumLink(
      generateUrl(ROUTES.COMPANY_TRACKER, { rootAppPath: settings.rootAppPath }),
      PERMISSION_GROUPS.COMPANY_TRACKER_LIGHT,
      AppIds.CompanyTrackerLight
    );
    if (isLoggedIn) {
      return accessLink();
    }

    const canRegister = settings.canRegister;
    if (canRegister) {
      const link = generateUrl(ROUTES.COMPANY_TRACKER_ONBOARDING, { onboardingPath: options?.onboardingPath ?? settings.rootAppPath });
      return redirect(link, options?.referralInfo);
    }

    toggleRequestDemoModal?.();
  }

  const goToCTLightInformation = () => {
    if (defaultConfig.flavour === AppFlavour.SINGAPORE) {
      return history.push(ROUTES.SGX_ESGENOME.path);
    }
    toggleRequestDemoModal?.();
  }

  return {
    GoToCompanyTrackerPro: () => premiumLink(generateUrl(ROUTES.COMPANY_TRACKER), CompanyTrackerIds, AppIds.CompanyTracker),
    GoToCompanyTrackerLight: () => premiumLink(
      generateUrl(ROUTES.COMPANY_TRACKER, { rootAppPath: settings.rootAppPath }),
      [PERMISSION_GROUPS.COMPANY_TRACKER_LIGHT, ...CompanyTrackerIds],
      AppIds.CompanyTrackerLight
    ),
    GoToCompanyTrackerRegistration,
    GoToCompanyTrackerRegistrationDirect: () => GoToCompanyTrackerRegistration({ onboardingPath: 'company-tracker' }),
    GoToCompanyTrackerLightInfo: () => goToCTLightInformation(),
    GoToPortfolioTracker: () => premiumLink(generateUrl(ROUTES.PORTFOLIO_TRACKER), PortfolioTrackerIds, AppIds.PortfolioTracker),
    GoToAssurance: () => authRequiredLink(generateUrl(ROUTES.ASSURANCE)),
    GoToSDGAP: () => authRequiredLink(generateUrl(ROUTES.SDGAP)),
    GoToWorldTracker: () => publicLink(generateUrl(ROUTES.WORLD_TRACKER)),
    ShowRequestADemo: () => toggleRequestDemoModal?.(),
  };
}
